import React from 'react';
import { Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { IndexaContribution } from '../../api/thirdparty/indexa/types';

interface IndexaContributionsListV2Props {
	contributions: IndexaContribution[];
	isLoading: boolean;
	error?: string;
	translate: (key: string) => string;
}

const IndexaContributionsListV2: React.FC<IndexaContributionsListV2Props> = ({
	contributions,
	isLoading,
	error,
	translate
}) => {
	if (isLoading) {
		return (
			<div className="flex flex-col items-center justify-center gap-4 py-8">
				<Loader2 className="w-8 h-8 animate-spin text-primary" />
				<p className="text-sm text-muted-foreground">
					{translate('indexaCapital.contributions.loading')}
				</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
				<p className="text-sm text-destructive">{error}</p>
			</div>
		);
	}

	if (!contributions || contributions.length === 0) {
		return (
			<div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 p-3">
				<p className="text-sm text-muted-foreground">
					{translate('indexaCapital.contributions.noData')}
				</p>
			</div>
		);
	}

	const formatAmount = (amount: number, currency: string) => {
		return new Intl.NumberFormat('es-ES', {
			style: 'currency',
			currency: currency
		}).format(amount);
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('es-ES', {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		});
	};

	const getOperationTypeText = (isTransfer: boolean) => {
		return isTransfer
			? translate('indexaCapital.contributions.transfer')
			: translate('indexaCapital.contributions.contribution');
	};

	// Agrupar por año
	const contributionsByYear = contributions.reduce(
		(acc, contribution) => {
			const year = contribution.year;
			if (!acc[year]) {
				acc[year] = [];
			}
			acc[year].push(contribution);
			return acc;
		},
		{} as Record<number, IndexaContribution[]>
	);

	// Ordenar años de forma descendente
	const sortedYears = Object.keys(contributionsByYear)
		.map(Number)
		.sort((a, b) => b - a);

	return (
		<div>
			<p className="text-base font-semibold text-foreground mb-4">
				{translate('indexaCapital.contributions.title')}
			</p>

			<div className="flex flex-col gap-6">
				{sortedYears.map((year) => {
					const yearContributions = contributionsByYear[year];
					const totalAmount = yearContributions.reduce((sum, c) => {
						const amount = typeof c.amount === 'string' ? parseFloat(c.amount) : c.amount;
						return sum + (isNaN(amount) ? 0 : amount);
					}, 0);

					return (
						<div key={year} className="rounded-lg border border-border bg-card p-4">
							<div className="flex items-center justify-between mb-4">
								<p className="text-lg font-bold text-foreground">{year}</p>
								<Badge className="bg-primary/10 text-primary border-primary/20 px-3 py-1 text-sm">
									{formatAmount(totalAmount, 'EUR')}
								</Badge>
							</div>

							<Table>
								<TableHeader>
									<TableRow>
										<TableHead className="text-xs">
											{translate('indexaCapital.contributions.date')}
										</TableHead>
										<TableHead className="text-xs">
											{translate('indexaCapital.contributions.type')}
										</TableHead>
										<TableHead className="text-xs min-w-[200px]">
											{translate('indexaCapital.contributions.instrument')}
										</TableHead>
										<TableHead className="text-xs text-right">
											{translate('indexaCapital.contributions.amount')}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{yearContributions
										.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
										.map((contribution) => (
											<TableRow key={contribution.id}>
												<TableCell className="text-sm">{formatDate(contribution.date)}</TableCell>
												<TableCell>
													<Badge
														className={
															contribution.isTransfer
																? 'bg-blue-100 text-blue-700 border-blue-200'
																: 'bg-primary/10 text-primary border-primary/20'
														}
													>
														{getOperationTypeText(contribution.isTransfer)}
													</Badge>
												</TableCell>
												<TableCell className="text-sm truncate max-w-[200px]">
													{contribution.instrumentName || contribution.description || '-'}
												</TableCell>
												<TableCell className="text-sm text-right font-medium">
													{formatAmount(contribution.amount, contribution.currency)}
												</TableCell>
											</TableRow>
										))}
								</TableBody>
							</Table>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default IndexaContributionsListV2;
